import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Drill, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (!res.ok) {
      setError(res.error);
      toast.error(res.error);
      return;
    }
    toast.success("Signed in");
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-[var(--bg)]" data-testid="login-page">
      {/* Left: Image panel */}
      <div className="hidden lg:flex lg:col-span-3 relative overflow-hidden">
        <img
          src="/well-drilling-hero.jpg"
          alt="Water well drilling crew on site"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(28,25,23,0.85)_0%,rgba(28,25,23,0.55)_45%,rgba(184,92,56,0.35)_100%)]" />
        <div className="relative z-10 flex flex-col justify-between p-10 lg:p-14 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 grid place-items-center bg-white/10 backdrop-blur-sm border border-white/20">
              <Drill className="h-5 w-5" />
            </div>
            <div className="font-display text-lg tracking-tight">JUBILEE BOARD</div>
          </div>

          <div className="max-w-xl">
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-white/70 mb-4">
              / private board portal
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
              Coordinate vendor &amp; attendee outreach,
              <span className="text-[#F2C2A0]"> without overlap.</span>
            </h1>
            <p className="mt-6 text-white/80 max-w-lg leading-relaxed">
              A 13-seat workspace for the board to claim contacts, log calls, and keep state-by-state
              outreach moving — Air Rotary, Mud Rotary, and Environmental, in one place.
            </p>
          </div>

          <div className="flex items-center gap-6 text-xs font-mono uppercase tracking-widest text-white/60">
            <span>Air Rotary</span>
            <span className="h-px w-6 bg-white/30" />
            <span>Mud Rotary</span>
            <span className="h-px w-6 bg-white/30" />
            <span>Environmental</span>
          </div>
        </div>
      </div>

      {/* Right: Login form */}
      <div className="lg:col-span-2 flex items-center justify-center p-8 bg-grain">
        <form
          onSubmit={submit}
          className="w-full max-w-sm space-y-7 animate-fade-up"
          data-testid="login-form"
        >
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-stone-500 mb-3">
              / sign in
            </p>
            <h2 className="font-display text-3xl font-bold tracking-tight text-stone-900">
              Welcome back, board member.
            </h2>
            <p className="mt-2 text-sm text-stone-600">
              Use the email associated with your board account.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-mono uppercase tracking-wider text-stone-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 h-11 rounded-sm border-stone-300 bg-white focus-visible:ring-[#B85C38] focus-visible:ring-offset-0"
                data-testid="login-email-input"
                placeholder="you@board.org"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-mono uppercase tracking-wider text-stone-700">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 h-11 rounded-sm border-stone-300 bg-white focus-visible:ring-[#B85C38] focus-visible:ring-offset-0"
                data-testid="login-password-input"
              />
            </div>
          </div>

          {error && (
            <div
              className="text-sm border border-red-200 bg-red-50 text-red-800 px-3 py-2 rounded-sm"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 rounded-sm bg-[#B85C38] hover:bg-[#9A4D2E] text-white font-medium transition-colors group"
            data-testid="login-submit-btn"
          >
            {loading ? "Signing in…" : (
              <span className="flex items-center justify-center gap-2">
                Enter dashboard
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            )}
          </Button>

          <p className="text-xs font-mono text-stone-500 pt-4 border-t border-stone-200">
            Restricted access. Contact your board administrator to request credentials.
          </p>
        </form>
      </div>
    </div>
  );
}
